import React from "react";
import { ChevronDown } from "lucide-react";
import { fmtEur } from "@/lib/vg2Format";

const REL = { HIGH: "Buona", MEDIUM: "Media", LOW: "Bassa" };
const STATE = { da_ristrutturare: "da ristrutturare", buono: "buono", ottimo: "ottimo", ordinario: "ordinario", nuovo: "nuovo" };
const GAP = [
  [/^componente_mercato/, "Il dato di mercato è un prezzo richiesto negli annunci, non un prezzo di compravendita."],
  [/^dato_mercato_datato_(\d+)/, (m) => `Il dato di mercato risale a ${m[1]} mesi fa: va aggiornato con annunci recenti.`],
  [/^dato_mercato_non_di_microzona/, "Il dato di mercato si riferisce a un territorio più ampio della microzona."],
];
const gapText = (g) => {
  for (const [re, t] of GAP) { const m = g.match(re); if (m) return typeof t === "function" ? t(m) : t; }
  return null;
};
const eurSqm = (v) => (v ? `${fmtEur(v)}/m²` : "—");

const _host = (u) => { try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return null; } };

function SourceRow({ s, i }) {
  const official = s.kind === "omi";
  const nature = official ? "quotazione ufficiale OMI" : "prezzi richiesti negli annunci (non quotazioni ufficiali)";
  // I titoli delle pagine web («quotazioni immobili…») non devono far sembrare ufficiale un prezzo richiesto.
  const name = official ? s.name : (_host(s.url) || (/quotazion/i.test(s.name || "") ? "Portale immobiliare" : s.name));
  const val = s.value_low && s.value_high && s.value_low !== s.value_high && s.kind === "omi"
    ? `${eurSqm(s.value_low)} – ${eurSqm(s.value_high)}` : eurSqm(s.value_central);
  return (
    <li className="py-2 border-b border-vg2-line last:border-0" data-testid={`method-source-${i}`}>
      <p className="text-sm text-vg2-ink">
        {s.url ? <a href={s.url} target="_blank" rel="noreferrer" className="underline">{name}</a> : name}
        <span className="text-vg2-muted"> · {nature}</span>
        {s.used_in_calculation === false && <span className="text-vg2-muted"> · solo come riferimento, non entra nel calcolo</span>}
      </p>
      <p className="text-xs text-vg2-muted">
        {s.territory}{s.period ? ` · periodo ${s.period}` : ""}{s.state_used ? ` · stato ${s.state_used.toLowerCase()}` : ""} · {val}
      </p>
    </li>
  );
}

export function RuleRefreshNotice({ result }) {
  if (result?.rule_outdated) {
    return (
      <p className="mb-6 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm" data-testid="valuation-rule-outdated">
        Questa valutazione è stata calcolata con regole precedenti e non è confrontabile con le stime attuali: avvia una nuova valutazione per avere numeri aggiornati.
      </p>
    );
  }
  if (result?.rule_refresh) {
    return (
      <p className="mb-6 text-xs text-vg2-muted" data-testid="valuation-rule-refreshed">
        Valore aggiornato alla regola vigente sullo stato dell'immobile (stesse fonti della valutazione originale; valore calcolato in origine {fmtEur(result.rule_refresh.stored_central)}).
      </p>
    );
  }
  return null;
}

export function ValuationMethodDetails({ trace, declaredCondition }) {
  const [open, setOpen] = React.useState(false);
  if (!trace?.result?.central) return null;
  const st = Object.fromEntries((trace.steps || []).map((s) => [s.step, s]));
  const integ = st.integration || {};
  const blended = integ.method === "omi_web_blend_50_50";
  const cond = st.condition || {};
  const gaps = (trace.gaps || []).map(gapText).filter(Boolean);
  return (
    <section className="mt-8 mb-8 rounded-xl border border-vg2-line bg-white" data-testid="valuation-method">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="valuation-method-toggle"
              className="w-full flex items-center justify-between px-5 py-4 text-left">
        <span className="text-sm font-medium">Come abbiamo calcolato il valore</span>
        <ChevronDown size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm" data-testid="valuation-method-body">
          <p className="text-xs uppercase tracking-[0.12em] text-vg2-muted mb-1">Fonti</p>
          <ul className="mb-4">{(trace.sources || []).map((s, i) => <SourceRow key={i} s={s} i={i} />)}</ul>
          <p className="text-xs uppercase tracking-[0.12em] text-vg2-muted mb-1">Passaggi</p>
          <ol className="list-decimal pl-5 space-y-1 mb-4" data-testid="valuation-method-steps">
            <li>Quotazione ufficiale della zona, per immobili in stato normale: <strong>{eurSqm(st.omi_component?.unit_value)}</strong></li>
            {blended && <li>Prezzi richiesti negli annunci della zona: <strong>{eurSqm(st.market_component?.unit_value)}</strong></li>}
            <li>
              {blended ? "Valore di riferimento che combina quotazione ufficiale e prezzi richiesti negli annunci" : "Valore di riferimento dalla sola quotazione ufficiale (dato di mercato non disponibile o non confrontabile)"}:
              {" "}<strong>{eurSqm(integ.unit_value)}</strong>
            </li>
            {cond.factor && cond.factor !== 1 && (
              <li>Correzione per lo stato dichiarato ({STATE[cond.condition] || cond.condition}), applicata una sola volta: <strong>{eurSqm(cond.unit_value)}</strong></li>
            )}
            {cond.factor === 1 && cond.condition && (
              <li data-testid="valuation-method-no-state-correction">
                {declaredCondition && declaredCondition !== (STATE[cond.condition] || cond.condition)
                  ? <>Stato dichiarato: {declaredCondition}. Nel calcolo è trattato come «{STATE[cond.condition] || cond.condition}»: nessuna correzione, il valore di riferimento è già quello dell'immobile nello stato attuale.</>
                  : <>Stato dichiarato ({STATE[cond.condition] || cond.condition}): nessuna correzione, il valore di riferimento è già quello dell'immobile nello stato attuale.</>}
              </li>
            )}
            <li>Per {Math.round(trace.surface_sqm)} m²: <strong>{fmtEur(trace.result.central)}</strong></li>
          </ol>
          <div className="grid grid-cols-3 gap-2 mb-4 text-center" data-testid="valuation-method-range">
            {[["Prudente", trace.result.low], ["Centrale", trace.result.central], ["Alto", trace.result.high]].map(([l, v]) => (
              <div key={l} className="rounded-lg bg-vg2-subtle p-2"><p className="text-xs text-vg2-muted">{l}</p><p className="font-semibold">{fmtEur(v)}</p></div>
            ))}
          </div>
          <p data-testid="valuation-method-reliability">
            Affidabilità: <strong>{trace.reliability === "HIGH" && (trace.gaps || []).some((g) => g.startsWith("dato_mercato")) ? "Media (dati di mercato da aggiornare)" : (REL[trace.reliability] || trace.reliability)}</strong>
            {trace.fallback_used ? " · stima esplorativa con fonte di riserva" : ""}
          </p>
          {gaps.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-xs text-amber-800 space-y-0.5" data-testid="valuation-method-gaps">
              {gaps.map((g) => <li key={g}>{g}</li>)}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
